import { api } from '../../lib/api.js';
import { BaseComponent, html, nothing } from '../../lib/lit.js';
import { notificationIcon, notificationLink, notificationText } from '../../lib/notifications.js';
import { router } from '../../lib/router.js';
import { formatItemDate } from './community-item-list.js';

/**
 * <community-notifications> — everything you have been told about, newest
 * first.
 *
 * Opening a notification marks it read and goes to the thing it is about.
 * Whenever the unread count may have changed this fires `unread-changed`, so
 * the app can refresh its badge rather than wait for the next poll.
 */
export class CommunityNotifications extends BaseComponent {
    static properties = {
        _notifications: { state: true },
        _nextBefore: { state: true },
        _loading: { state: true },
        _loadingMore: { state: true },
        _markingAll: { state: true },
        _error: { state: true },
    };

    constructor() {
        super();
        /** @type {Array<object>} */
        this._notifications = [];
        this._nextBefore = '';
        this._loading = true;
        this._loadingMore = false;
        this._markingAll = false;
        this._error = '';
    }

    connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    async _load() {
        this._loading = true;
        this._error = '';
        try {
            const page = await api.get('/notifications');
            this._notifications = page?.notifications ?? [];
            this._nextBefore = page?.nextBefore ?? '';
        } catch (err) {
            this._error = String(/** @type {any} */ (err).message ?? err);
        } finally {
            this._loading = false;
        }
    }

    async _loadMore() {
        if (!this._nextBefore || this._loadingMore) return;
        this._loadingMore = true;
        try {
            const page = await api.get(
                `/notifications?before=${encodeURIComponent(this._nextBefore)}`,
            );
            this._notifications = [...this._notifications, ...(page?.notifications ?? [])];
            this._nextBefore = page?.nextBefore ?? '';
        } catch (err) {
            this._error = String(/** @type {any} */ (err).message ?? err);
        } finally {
            this._loadingMore = false;
        }
    }

    /**
     * Mark it read and go. The read is not awaited before navigating: the
     * person asked to see the thing, and a slow write should not hold that up.
     * @param {object} notification
     */
    _open(notification) {
        if (!notification.read) {
            this._notifications = this._notifications.map((n) =>
                n.id === notification.id ? { ...n, read: true } : n,
            );
            api.post(`/notifications/${notification.id}/read`)
                .then(() => this._announceUnreadChanged())
                .catch(() => {
                    // Not worth interrupting navigation for. The badge will
                    // say it is still unread on the next poll.
                });
        }
        router.navigate(notificationLink(notification));
    }

    async _markAllRead() {
        this._markingAll = true;
        this._error = '';
        try {
            await api.post('/notifications/read-all');
            this._notifications = this._notifications.map((n) => ({ ...n, read: true }));
            this._announceUnreadChanged();
        } catch (err) {
            this._error = String(/** @type {any} */ (err).message ?? err);
        } finally {
            this._markingAll = false;
        }
    }

    _announceUnreadChanged() {
        this.dispatchEvent(new CustomEvent('unread-changed', { bubbles: true, composed: true }));
    }

    _renderNotification(notification) {
        return html`
            <button
                type="button"
                class="list-group-item list-group-item-action d-flex gap-3 align-items-start py-3 ${
                    notification.read ? '' : 'bg-primary-subtle'
                }"
                @click=${() => this._open(notification)}
            >
                <i class="bi ${notificationIcon(notification)} text-secondary mt-1"></i>
                <div class="flex-grow-1 text-start">
                    <div class=${notification.read ? '' : 'fw-semibold'}>
                        ${notificationText(notification)}
                    </div>
                    <div class="text-muted small">${formatItemDate(notification.createdAt)}</div>
                </div>
                ${
                    notification.read
                        ? nothing
                        : html`<span class="badge rounded-pill text-bg-primary mt-1">New</span>`
                }
            </button>
        `;
    }

    render() {
        if (this._loading) {
            return html`
                <div class="text-muted small d-flex align-items-center gap-2">
                    <span class="spinner-border spinner-border-sm" role="status"></span>
                    Loading…
                </div>
            `;
        }

        const anyUnread = this._notifications.some((n) => !n.read);

        return html`
            <div class="d-flex align-items-center mb-3 gap-2">
                <h1 class="h4 mb-0">Notifications</h1>
                <div class="ms-auto d-flex gap-2">
                    <button
                        class="btn btn-sm btn-outline-secondary"
                        ?disabled=${!anyUnread || this._markingAll}
                        @click=${this._markAllRead}
                    >
                        <i class="bi bi-check2-all me-1"></i>Mark all read
                    </button>
                    <button
                        class="btn btn-sm btn-outline-secondary"
                        @click=${() => router.navigate('/settings')}
                    >
                        <i class="bi bi-gear me-1"></i>Email settings
                    </button>
                </div>
            </div>
            ${this._error ? html`<div class="alert alert-danger" role="alert">${this._error}</div>` : nothing}
            ${
                this._notifications.length === 0
                    ? html`<p class="text-muted">
                          Nothing yet. You'll hear here when someone replies to feedback you
                          follow, when it's closed or reopened, when a changelog post credits it,
                          when a new poll opens, and when results are in for a poll you answered.
                      </p>`
                    : html`<div class="list-group shadow-sm mb-3">
                          ${this._notifications.map((n) => this._renderNotification(n))}
                      </div>`
            }
            ${
                this._nextBefore
                    ? html`<button
                          class="btn btn-link px-0"
                          ?disabled=${this._loadingMore}
                          @click=${this._loadMore}
                      >
                          Older notifications
                      </button>`
                    : nothing
            }
        `;
    }
}

customElements.define('community-notifications', CommunityNotifications);
